import * as React from 'react';

import { cn } from '@/shared/lib/utils/classnames';

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, type, style, onFocus, onBlur, 'aria-invalid': ariaInvalid, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const combinedRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    // 에러 상태 감지 및 스타일 적용
    React.useEffect(() => {
      if (inputRef.current) {
        const isInvalid =
          ariaInvalid === true || inputRef.current.getAttribute('aria-invalid') === 'true';
        if (isInvalid) {
          // 에러 상태: red border
          inputRef.current.style.setProperty('border-color', '#ef4444', 'important');
        } else if (!inputRef.current.disabled && document.activeElement !== inputRef.current) {
          // 에러가 아니고 포커스도 아닐 때: 기본 border 색상
          inputRef.current.style.removeProperty('border-color');
        }
      }
    }, [ariaInvalid]);

    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        // 전역 CSS의 !important를 오버라이드하기 위해 직접 스타일 설정
        if (inputRef.current) {
          const isInvalid =
            ariaInvalid === true || inputRef.current.getAttribute('aria-invalid') === 'true';
          const computedStyle = window.getComputedStyle(inputRef.current);
          // border-radius를 먼저 명시적으로 유지 (렌더링 문제 방지)
          const borderRadius = computedStyle.borderRadius || '6px';
          inputRef.current.style.setProperty('border-radius', borderRadius, 'important');

          // Focus: 에러가 아니면 black border, 에러면 red border 유지
          // border-color만 변경하여 border-radius 렌더링 문제 방지
          if (!isInvalid) {
            inputRef.current.style.setProperty('border-color', '#000000', 'important');
          } else {
            inputRef.current.style.setProperty('border-color', '#ef4444', 'important');
          }
          // Ring, outline 완전히 제거
          inputRef.current.style.setProperty('outline', 'none', 'important');
          inputRef.current.style.setProperty('outline-width', '0', 'important');
          inputRef.current.style.setProperty('outline-style', 'none', 'important');
          inputRef.current.style.setProperty('outline-offset', '0', 'important');
          inputRef.current.style.setProperty('box-shadow', 'none', 'important');
          inputRef.current.style.setProperty('--tw-ring-offset-shadow', 'none', 'important');
          inputRef.current.style.setProperty('--tw-ring-shadow', 'none', 'important');
        }
        onFocus?.(e);
      },
      [onFocus, ariaInvalid],
    );

    const handleBlur = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        // Blur 시 에러 상태에 따라 border 색상 설정
        if (inputRef.current) {
          const isInvalid =
            ariaInvalid === true || inputRef.current.getAttribute('aria-invalid') === 'true';
          const computedStyle = window.getComputedStyle(inputRef.current);
          // border-radius를 먼저 명시적으로 유지 (렌더링 문제 방지)
          const borderRadius = computedStyle.borderRadius || '6px';
          inputRef.current.style.setProperty('border-radius', borderRadius, 'important');

          if (isInvalid) {
            // 에러 상태: red border 유지
            inputRef.current.style.setProperty('border-color', '#ef4444', 'important');
          } else {
            // 기본 상태: border 색상 제거 (기본값으로 복원)
            inputRef.current.style.removeProperty('border-color');
          }
          // Ring, outline 제거 유지
          inputRef.current.style.setProperty('outline', 'none', 'important');
          inputRef.current.style.setProperty('box-shadow', 'none', 'important');
        }
        onBlur?.(e);
      },
      [onBlur, ariaInvalid],
    );

    return (
      <input
        type={type}
        className={cn(
          // 기본 스타일
          'flex h-9 w-full rounded-md border border-[#d9dde0] bg-white px-3 py-1 text-base text-black',
          // Placeholder 스타일 (light gray)
          'placeholder:text-[#9ca3af]',
          // Ring, outline 완전히 제거 (모든 상태에서)
          'outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
          // Disabled 상태: light gray background, light gray border
          'disabled:bg-[#f3f4f6] disabled:border-[#d9dde0] disabled:cursor-not-allowed disabled:text-[#9ca3af]',
          // Error 상태 (aria-invalid 속성 사용 시) - red border
          'aria-invalid:border-red-500',
          // Transition (border color만)
          'transition-colors',
          // File input 스타일
          'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
          // 반응형
          'md:text-sm',
          className,
        )}
        style={{
          ...style,
          // 기본적으로 ring, outline 제거
          outline: 'none',
          boxShadow: 'none',
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-invalid={ariaInvalid}
        ref={combinedRef}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';

export { Input };
